import React, { useEffect, useRef, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { Modal, ModalBody, ModalFooter, ModalHeader } from 'reactstrap';
import toast from 'react-hot-toast';
import { Trash2Icon } from 'lucide-react';
import Api from 'services/api';
import { SubmitButton } from 'components/common/buttons/SubmitButton';
import ErrorMessage from 'components/common/ErrorMessage';

const CreateInterpreterPaymentStatementModal = ({
    isOpen,
    onClose,
    ids,
    filters,
    searchTerm,
    sortConfig,
}) => {
    const [loading, setLoading] = useState(false);
    const [dragActive, setDragActive] = useState(false);
    const inputRef = useRef(null);

    const methods = useForm({
        defaultValues: {
            file: undefined,
        },
    });
    const {
        control,
        formState: { errors },
        handleSubmit,
        setValue,
        watch,
        reset,
    } = methods;

    useEffect(() => {
        if (isOpen) {
            reset({ file: undefined });
            if (inputRef.current) inputRef.current.value = '';
        }
    }, [isOpen]);

    const handleDrag = (e) => {
        e.preventDefault();
        e.stopPropagation();
        if (e.type === 'dragenter' || e.type === 'dragover') {
            setDragActive(true);
        } else if (e.type === 'dragleave') {
            setDragActive(false);
        }
    };

    const handleBrowseClick = () => {
        inputRef.current.click();
    };

    const handleRemoveFile = () => {
        setValue('file', undefined);
        if (inputRef.current) inputRef.current.value = '';
    };

    // const onSubmit = async (data) => {
    //     setLoading(true);

    //     const formData = new FormData();
    //     (ids || []).forEach((id) => formData.append('ids[]', id));
    //     Object.entries({
    //         ...filters,
    //         search: searchTerm,
    //         sortBy: sortConfig?.sortBy,
    //         sortDirection: sortConfig?.sortDirection,
    //     }).forEach(([key, value]) => {
    //         if (value !== undefined && value !== null && value !== '') {
    //             formData.append(key, value);
    //         }
    //     });
    //     if (data?.file) formData.append('file', data.file);

    //     try {
    //         const response = await Api.createInterpreterPaymentStatement(formData);
    //         if (response?.status >= 200 && response?.status < 300) {
    //             const blob = new Blob([response.data], {
    //                 type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    //             });
    //             const url = window.URL.createObjectURL(blob);
    //             const link = document.createElement('a');
    //             link.href = url;
    //             link.download = 'interpreter-payments.xlsx';
    //             document.body.appendChild(link);
    //             link.click();
    //             link.remove();
    //             window.URL.revokeObjectURL(url);
    //             toast.success('Payment Statement Download Successfully');
    //             onClose();
    //         } else {
    //             console.log('Error response:', response);
    //             toast.error(response?.message || 'Statement generation failed.');
    //         }
    //     } catch (error) {
    //         toast.error(error?.response?.data?.message || 'Statement generation failed.');
    //     } finally {
    //         setLoading(false);
    //     }
    // };

    const onSubmit = async (data) => {
        setLoading(true);

        const formData = new FormData();
        (ids || []).forEach((id) => formData.append('ids[]', id));
        Object.entries({
            ...filters,
            search: searchTerm,
            sortBy: sortConfig?.sortBy,
            sortDirection: sortConfig?.sortDirection,
        }).forEach(([key, value]) => {
            if (value !== undefined && value !== null && value !== '') {
                formData.append(key, value);
            }
        });
        if (data?.file) formData.append('file', data.file);

        try {
            const response = await Api.createInterpreterPaymentStatement(formData);

            const isJsonError =
                response?.data instanceof Blob &&
                response?.data?.type === 'application/json';

            if (response?.status >= 200 && response?.status < 300 && !isJsonError) {
                const blob = new Blob([response.data], {
                    type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
                });
                const url = window.URL.createObjectURL(blob);
                const link = document.createElement('a');
                link.href = url;
                link.download = 'interpreter-payments.xlsx';
                document.body.appendChild(link);
                link.click();
                link.remove();
                window.URL.revokeObjectURL(url);
                toast.success('Payment Statement Download Successfully');
                onClose();
            } else {
                let errorMessage = 'Statement generation failed.';

                if (isJsonError) {
                    const errorText = await response.data.text();
                    try {
                        const errorJson = JSON.parse(errorText);
                        errorMessage = errorJson?.message || errorMessage;
                    } catch (parseErr) {
                        console.log('Failed to parse error blob:', parseErr);
                    }
                } else {
                    errorMessage = response?.data?.message || response?.message || errorMessage;
                }

                toast.error(errorMessage);
            }
        } catch (error) {
            if (error?.response?.data instanceof Blob && error.response.data.type === 'application/json') {
                const errorText = await error.response.data.text();
                try {
                    const errorJson = JSON.parse(errorText);
                    toast.error(errorJson?.message || 'Statement generation failed.');
                } catch {
                    toast.error('Statement generation failed.');
                }
            } else {
                toast.error(error?.response?.data?.message || 'Statement generation failed.');
            }
        } finally {
            setLoading(false);
        }
    };

    return (
        <Modal isOpen={isOpen} toggle={loading ? undefined : onClose} centered className="modal-medium">
            <form onSubmit={handleSubmit(onSubmit)}>
                <ModalHeader className="header_modal align-items-center">
                    <h5 className="modal-title_u">Download Statement</h5>
                </ModalHeader>
                <ModalBody className="modal_form_body">
                    {!watch('file') && (
                        <div className="step_cards min-height-auto">
                            <div className="row-primary">
                                <div style={{ width: '100%' }}>
                                    <Controller
                                        name="file"
                                        control={control}
                                        defaultValue={undefined}
                                        render={({ field }) => (
                                            <div
                                                onDragEnter={handleDrag}
                                                onDragOver={handleDrag}
                                                onDragLeave={handleDrag}
                                                onDrop={(e) => {
                                                    e.preventDefault();
                                                    e.stopPropagation();
                                                    setDragActive(false);
                                                    if (
                                                        e.dataTransfer.files &&
                                                        e.dataTransfer.files.length > 0
                                                    ) {
                                                        field.onChange(e.dataTransfer.files[0]);
                                                    }
                                                }}
                                                style={{
                                                    height: 240,
                                                    display: 'flex',
                                                    flexDirection: 'column',
                                                    alignItems: 'center',
                                                    justifyContent: 'center',
                                                    border: dragActive
                                                        ? '2px solid #1976d2'
                                                        : '2px dashed #ccc',
                                                    borderRadius: 8,
                                                    padding: 32,
                                                    textAlign: 'center',
                                                    marginTop: 16,
                                                    marginBottom: 10,
                                                    background: dragActive ? '#e3f2fd' : '#fafbff',
                                                    transition: 'all 0.2s',
                                                    position: 'relative',
                                                    outline: dragActive ? '2px solid #1976d2' : 'none',
                                                }}
                                            >
                                                <input
                                                    type="file"
                                                    ref={inputRef}
                                                    accept=".pdf,.doc,.docx,.jpg,.jpeg,.png,.xlsx,.xls"
                                                    style={{ display: 'none' }}
                                                    onChange={(e) => {
                                                        field.onChange(e.target.files[0]);
                                                    }}
                                                />
                                                <div
                                                    style={{
                                                        color: '#6F8DFF',
                                                        fontWeight: 600,
                                                        fontSize: 22,
                                                        marginBottom: 16,
                                                        display: 'flex',
                                                        flexDirection: 'column',
                                                        alignItems: 'center',
                                                        justifyContent: 'center',
                                                        gap: 8,
                                                    }}
                                                >
                                                    Browse or Drag and Drop
                                                    <br />
                                                    <span
                                                        style={{
                                                            color: '#6c889c',
                                                            fontWeight: 400,
                                                            fontSize: 18,
                                                            marginLeft: 12,
                                                        }}
                                                    >
                                                        (.pdf, .doc, .docx, .jpg, .jpeg, .png, .xlsx, .xls)
                                                    </span>
                                                </div>
                                                <SubmitButton
                                                    btnText="Browse File"
                                                    buttonType="button"
                                                    handleSubmit={handleBrowseClick}
                                                />

                                                {errors?.file &&
                                                    errors?.file?.map((error) => (
                                                        <ErrorMessage text={error} />
                                                    ))}
                                            </div>
                                        )}
                                    />
                                </div>
                            </div>
                        </div>
                    )}

                    {watch('file') && (
                        <div
                            style={{
                                marginTop: 4,
                                marginBottom: 14,
                                borderRadius: 14,
                                overflow: 'hidden',
                                boxShadow: '0 2px 12px #e0e7ef',
                                background: '#f8fafc',
                                border: '1.5px solid #e0e7ef',
                            }}
                        >
                            <table
                                style={{
                                    width: '100%',
                                    borderCollapse: 'separate',
                                    borderSpacing: 0,
                                    background: '#f8fafc',
                                    fontSize: 15,
                                    color: '#6c889c',
                                }}
                            >
                                <thead>
                                    <tr style={{ background: '#f4f7fb' }}>
                                        <th
                                            style={{
                                                padding: '16px 8px',
                                                fontWeight: 700,
                                                textAlign: 'left',
                                                color: '#6c889c',
                                                borderBottom: '1.5px solid #e0e7ef',
                                                borderLeft: '1.5px solid #e0e7ef',
                                            }}
                                        >
                                            File
                                        </th>
                                        <th
                                            style={{
                                                padding: '16px 8px',
                                                fontWeight: 700,
                                                textAlign: 'left',
                                                color: '#6c889c',
                                                borderBottom: '1.5px solid #e0e7ef',
                                            }}
                                        >
                                            Actions
                                        </th>
                                    </tr>
                                </thead>
                                <tbody>
                                    <tr style={{ background: '#f8fafc' }}>
                                        <td
                                            style={{
                                                padding: '14px 8px',
                                                borderBottom: '1px solid #e0e7ef',
                                                borderRight: '1.5px solid #e0e7ef',
                                            }}
                                        >
                                            {watch('file')?.name}
                                        </td>
                                        <td
                                            style={{
                                                padding: '14px 8px',
                                                textAlign: 'center',
                                                borderBottom: '1px solid #e0e7ef',
                                            }}
                                        >
                                            <span
                                                onClick={handleRemoveFile}
                                                style={{
                                                    color: '#d32f2f',
                                                    cursor: 'pointer',
                                                    fontWeight: 600,
                                                    textDecoration: 'underline',
                                                    padding: '8px 10px',
                                                    borderRadius: 6,
                                                    transition: 'background 0.15s',
                                                    userSelect: 'none',
                                                    display: 'inline-block',
                                                }}
                                                onMouseOver={(e) =>
                                                    (e.currentTarget.style.background = '#ffeaea')
                                                }
                                                onMouseOut={(e) =>
                                                    (e.currentTarget.style.background = 'transparent')
                                                }
                                                role="button"
                                                tabIndex={0}
                                            >
                                                <Trash2Icon size={18} />
                                            </span>
                                        </td>
                                    </tr>
                                </tbody>
                            </table>
                        </div>
                    )}
                </ModalBody>
                <ModalFooter>
                    <SubmitButton
                        buttonType="submit"
                        loader={loading}
                        className="main_button"
                        btnText={loading ? 'Generating...' : 'Download Statement'}
                        handleSubmit={handleSubmit(onSubmit)}
                    />
                    <button type="button" className="table_delete" onClick={onClose} disabled={loading}>
                        Cancel
                    </button>
                </ModalFooter>
            </form>
        </Modal>
    );
};

export default CreateInterpreterPaymentStatementModal;